/**
 * App-wide feed of order status changes from WS `orderUpdates` (no snapshot; events only).
 *
 * The future order-ticket / toasts ticket (#678 and friends) subscribes here:
 *   const off = orderEvents.subscribe((e) => toast(`${e.update.order.coin} ${e.update.status}`));
 *
 * Producers: OrderToasts and the user session feed frames in. Both can hold the same
 * user (they share one HL subscription but each gets the frame), so events are de-duplicated by
 * (user, oid, status, statusTimestamp) before listeners see them.
 */
import type { OrderUpdate } from '../../hl/types';

export interface OrderEvent {
  /** Lowercase user address the event belongs to. */
  user: string;
  update: OrderUpdate;
  /** status === 'open' adds an open order; every other status removes it (spec section 1). */
  kind: 'added' | 'removed';
}

export type OrderEventListener = (e: OrderEvent) => void;

export class OrderEventEmitter {
  private listeners = new Set<OrderEventListener>();
  private seen = new Set<string>();
  private seenOrder: string[] = [];

  constructor(private readonly dedupeWindow = 2_000) {}

  subscribe(listener: OrderEventListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  get listenerCount(): number {
    return this.listeners.size;
  }

  /** Feed one orderUpdates frame. Returns the events actually emitted (duplicates dropped). */
  emit(user: string, updates: readonly OrderUpdate[]): OrderEvent[] {
    const u = user.toLowerCase();
    const out: OrderEvent[] = [];
    for (const update of updates) {
      const key = `${u}:${update.order.oid}:${update.status}:${update.statusTimestamp}`;
      if (this.seen.has(key)) continue;
      this.remember(key);
      out.push({ user: u, update, kind: update.status === 'open' ? 'added' : 'removed' });
    }
    for (const e of out) {
      for (const l of [...this.listeners]) {
        try {
          l(e);
        } catch (err) {
          console.error('orderEvents listener failed', err);
        }
      }
    }
    return out;
  }

  /** Test helper: forget de-dup state and listeners. */
  reset(): void {
    this.listeners.clear();
    this.seen.clear();
    this.seenOrder = [];
  }

  private remember(key: string): void {
    this.seen.add(key);
    this.seenOrder.push(key);
    if (this.seenOrder.length > this.dedupeWindow) {
      const drop = this.seenOrder.splice(0, this.seenOrder.length - this.dedupeWindow);
      for (const k of drop) this.seen.delete(k);
    }
  }
}

/** The shared app-wide emitter. */
export const orderEvents = new OrderEventEmitter();
